import type { BotConfig } from "@investhinks/api-client";
import moment from "moment";
import React, { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Container, Row } from "reactstrap";

import { adminStatsEndpoints, upcomingChargesEndpoints } from "../../../../api";
import AttentionQueue, { type AttentionItem } from "../../../../components/admin/AttentionQueue";
import { isNearStopLoss, NEAR_STOP_LOSS_RATIO } from "../../../../utils/nearStopLoss";
import KpiTile from "../../../../components/admin/KpiTile";
import ServerBuildAlert from "../../../../components/admin/ServerBuildAlert";
import PageHeader from "../../../../components/admin/PageHeader";
import MonthlyEarningsGraph from "../../../../components/adminStats/graphs/MonthlyEarningsGraph";
import useBots from "../../../../components/bots/hooks/useBots";
import { groupByUser, loadAllUnpaidCharges } from "../../../../components/charges/unpaidCharges";
import useManualPaymentInvoices from "../../../../components/manualPayments/hooks/useManualPaymentInvoices";
import useFetch from "../../../../hooks/useFetch";

const FAILED_AGE_DAYS = 7;
const STALE_SNAPSHOT_HOURS = 2;
const UPCOMING_WINDOW_DAYS = 7;

const ACTIVE_BOT_FILTERS = {
  disabled: { $ne: true as const },
  user: { isTest: { $ne: true as const } },
  strategy: { $notNull: true as const },
  id: { $ne: 49 },
  exchange_account: { status: { $ne: "INVALID" as const } },
  $or: [{ exchange_account: { hidden: false } }, { exchange_account: { hidden: { $null: true } } }],
};

const eur = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const pct = (curr: number, prev: number): { text: string; tone: "ok" | "crit" | "neutral" } | undefined => {
  if (!prev) return undefined;
  const d = ((curr - prev) / prev) * 100;
  const tone = d > 0.5 ? "ok" : d < -0.5 ? "crit" : "neutral";
  return { text: `${d >= 0 ? "▲" : "▼"} ${Math.abs(d).toFixed(1)}%`, tone };
};

const Overview: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.overview" });
  const now = moment();
  const year = now.year();
  const monthKey = String(now.month() + 1);
  const prevMonth = now.clone().subtract(1, "month");

  // --- Revenue -------------------------------------------------------------
  // Compare only days that are complete in BOTH months, so the running month
  // is never measured against a longer span than it has lived. Today itself is
  // excluded because only part of it has elapsed.
  //
  // The number of days comes from the browser; the day boundaries themselves
  // are UTC, server-side. Both months are sliced identically, so the
  // comparison stays like-for-like, but in a non-UTC timezone the current
  // month's slice can be short by up to the offset.
  const completedDays = now.date() - 1;
  const comparableDays = Math.min(completedDays, prevMonth.daysInMonth());
  const canCompare = comparableDays >= 1;

  const { data: chargesYear, isLoading: loadingCharges } = useFetch(
    () => adminStatsEndpoints.getYearMonthlyCharges(year, comparableDays).then(({ data }) => data),
    { cacheKey: `overview_charges_${year}_d${comparableDays}` }
  );
  const { data: chargesPrevYear } = useFetch(
    () => adminStatsEndpoints.getYearMonthlyCharges(year - 1, comparableDays).then(({ data }) => data),
    { cacheKey: `overview_charges_${year - 1}_d${comparableDays}` }
  );
  const thisMonth = chargesYear?.total?.perMonth?.[monthKey];
  const prevMonthData =
    prevMonth.year() === year
      ? chargesYear?.total?.perMonth?.[String(prevMonth.month() + 1)]
      : chargesPrevYear?.total?.perMonth?.[String(prevMonth.month() + 1)];
  // The headline figure stays month-to-date; only the comparison is sliced.
  const mrr = thisMonth?.charged ?? 0;
  const mrrSameSpan = thisMonth?.chargedThroughDay;
  const mrrPrevSameSpan = prevMonthData?.chargedThroughDay;
  // Whether a like-for-like comparison was possible at all. Kept separate
  // from the delta, because a previous span of exactly zero is comparable:
  // it just has no percentage.
  const canShowComparison = canCompare && mrrSameSpan !== undefined && mrrPrevSameSpan !== undefined;
  // No fabricated delta: on the 1st nothing is complete to compare, and an API
  // without `chargedThroughDay` cannot be guessed at, because revenue clusters
  // on renewal days and prorating misleads.
  const mrrDelta = !canShowComparison
    ? undefined
    : mrrPrevSameSpan
      ? pct(mrrSameSpan as number, mrrPrevSameSpan)
      : (mrrSameSpan ?? 0) > 0
        ? // Nothing charged in the same span last month, so a percentage is
          // undefined rather than infinite.
          ({ text: `▲ ${t("kpi.mrrNoBaseline")}`, tone: "ok" } as const)
        : undefined;

  // --- Unpaid ----------------------------------------------------------------
  const {
    data: unpaidCharges,
    isLoading: loadingUnpaid,
    isRefreshing: refreshingUnpaid,
    error: unpaidError,
  } = useFetch(loadAllUnpaidCharges, {
    cacheKey: "missing_payments_charges",
  });
  const unpaid = useMemo(() => {
    const groups = groupByUser(unpaidCharges ?? []);
    const cutoff = moment().subtract(FAILED_AGE_DAYS, "days");
    return {
      users: groups.length,
      amountEur: groups.reduce((s, g) => s + g.unpaidEur, 0),
      escalated: groups.filter((g) => g.isEscalated || g.needsAttention).length,
      noCard: groups.filter((g) => !g.hasValidCard || g.isCardExpired).length,
      failedOld: groups.reduce(
        (s, g) =>
          s + g.charges.filter((c) => c.status === "failed" && c.lastAttemptAt && moment(c.lastAttemptAt).isBefore(cutoff)).length,
        0
      ),
      failed: groups.reduce((s, g) => s + g.failedCount, 0),
    };
  }, [unpaidCharges]);

  // --- Bots ------------------------------------------------------------------
  const { bots, pagination: botsPagination, isLoading: loadingBots, error: botsError } = useBots({
    filters: ACTIVE_BOT_FILTERS,
    populate: ["stats"],
    pagination: { pageSize: 100 },
    sort: {},
    cacheKey: "overview_bots",
  });
  const { pagination: onLossPagination, isLoading: loadingOnLoss, error: onLossError } = useBots({
    filters: { ...ACTIVE_BOT_FILTERS, onLoss: true },
    populate: [],
    pagination: { pageSize: 1 },
    sort: {},
  });
  // Counted server-side rather than from the 100-row sample below, so the
  // badge matches the number of rows the deep link actually shows.
  const { pagination: stalePagination, isLoading: loadingStale, error: staleError } = useBots({
    filters: { ...ACTIVE_BOT_FILTERS, staleSnapshotHours: STALE_SNAPSHOT_HOURS },
    populate: [],
    pagination: { pageSize: 1 },
    sort: {},
  });
  const botStats = useMemo(() => {
    const list: BotConfig[] = bots ?? [];
    return {
      total: botsPagination?.total ?? list.length,
      sampled: list.length,
      // Left undefined when the count did not arrive, so a failure is not
      // indistinguishable from "none".
      stale: stalePagination?.total,
      nearStopLoss: list.filter((bot) => isNearStopLoss(bot.stats)),
    };
  }, [bots, botsPagination, stalePagination]);

  // --- Sign-ups (last 30 days) ----------------------------------------------
  const { data: signupsThis, isLoading: loadingSignups } = useFetch(
    () => adminStatsEndpoints.getMonthDailySignUps(now.format("YYYY-MM")).then(({ data }) => data),
    { cacheKey: `overview_signups_${now.format("YYYY-MM")}` }
  );
  const { data: signupsPrev } = useFetch(
    () => adminStatsEndpoints.getMonthDailySignUps(prevMonth.format("YYYY-MM")).then(({ data }) => data),
    { cacheKey: `overview_signups_${prevMonth.format("YYYY-MM")}` }
  );
  const { data: signupsPrev2 } = useFetch(
    () =>
      adminStatsEndpoints
        .getMonthDailySignUps(now.clone().subtract(2, "months").format("YYYY-MM"))
        .then(({ data }) => data),
    { cacheKey: `overview_signups_${now.clone().subtract(2, "months").format("YYYY-MM")}` }
  );
  const signups = useMemo(() => {
    const sumWindow = (from: moment.Moment, to: moment.Moment) => {
      let total = 0;
      let clients = 0;
      const months = [
        [now, signupsThis],
        [prevMonth, signupsPrev],
        [now.clone().subtract(2, "months"), signupsPrev2],
      ] as const;
      for (const [m, data] of months) {
        for (const [day, d] of Object.entries(data?.perDay ?? {})) {
          const date = m.clone().date(Number(day));
          if (date.isSameOrAfter(from, "day") && date.isSameOrBefore(to, "day")) {
            total += d.total ?? 0;
            clients += d.clients ?? 0;
          }
        }
      }
      return { total, clients };
    };
    const last30 = sumWindow(now.clone().subtract(29, "days"), now);
    const prior30 = sumWindow(now.clone().subtract(59, "days"), now.clone().subtract(30, "days"));
    return { ...last30, prior: prior30.total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupsThis, signupsPrev, signupsPrev2]);

  // --- Upcoming charges ------------------------------------------------------
  const {
    data: upcoming,
    isLoading: loadingUpcoming,
    isRefreshing: refreshingUpcoming,
    error: upcomingError,
  } = useFetch(() => upcomingChargesEndpoints.getUpcomingCharges(), {
    cacheKey: "upcomingCharges_overview",
  });
  const upcomingWindow = useMemo(() => {
    const list = (upcoming?.charges ?? []).filter((c) => c.billing.daysUntilDue <= UPCOMING_WINDOW_DAYS);
    return {
      count: list.length,
      amount: list.reduce((s, c) => s + (c.estimatedAmount ?? 0), 0),
      failedSubs: list.filter((c) => c.subscription.pm_status !== "active").length,
    };
  }, [upcoming]);

  // --- Manual invoices -------------------------------------------------------
  // Held in a ref-stable string so the request key does not change every render.
  const overdueCutoff = useMemo(() => moment().startOf("day").toISOString(), []);
  const { pagination: manualPagination, isLoading: loadingManual, error: manualError } = useManualPaymentInvoices({
    pagination: { pageSize: 1 },
  });
  // Counted server-side rather than from a 100-row sample, so the badge
  // matches the number of rows the "past due only" deep link shows.
  const { pagination: overduePagination, isLoading: loadingOverdue, error: overdueError } = useManualPaymentInvoices({
    pagination: { pageSize: 1 },
    extraParams: { dueBefore: overdueCutoff },
  });
  const manual = useMemo(
    () => ({ total: manualPagination?.total ?? 0, overdue: overduePagination?.total }),
    [manualPagination, overduePagination]
  );

  // --- Attention queue -------------------------------------------------------
  const attention: AttentionItem[] = [];
  if (unpaid.escalated > 0)
    attention.push({
      id: "escalated",
      severity: "crit",
      count: unpaid.escalated,
      title: t("attention.escalated"),
      to: "/billing/missing-payments?filter=escalated",
      destination: t("dest.missingPayments"),
    });
  if (unpaid.failedOld > 0)
    attention.push({
      id: "failedOld",
      severity: "crit",
      count: unpaid.failedOld,
      title: t("attention.failedOld", { days: FAILED_AGE_DAYS }),
      to: "/billing/missing-payments?filter=failed",
      destination: t("dest.missingPayments"),
    });
  if ((botStats.stale ?? 0) > 0)
    attention.push({
      id: "stale",
      severity: botStats.total > 0 && (botStats.stale ?? 0) > botStats.total / 2 ? "crit" : "warn",
      count: botStats.stale,
      title: t("attention.staleBots", { hours: STALE_SNAPSHOT_HOURS }),
      to: "/stats/bots?filter=stale",
      destination: t("dest.bots"),
    });
  if ((manual.overdue ?? 0) > 0)
    attention.push({
      id: "manualOverdue",
      severity: "warn",
      count: manual.overdue,
      title: t("attention.manualOverdue"),
      to: "/billing/manual-payments?filter=overdue",
      destination: t("dest.manualPayments"),
    });
  if (unpaid.noCard > 0)
    attention.push({
      id: "noCard",
      severity: "warn",
      count: unpaid.noCard,
      title: t("attention.noCard"),
      to: "/billing/missing-payments?filter=noPaymentMethod",
      destination: t("dest.missingPayments"),
    });
  if (upcomingWindow.failedSubs > 0)
    attention.push({
      id: "upcomingFailed",
      severity: "warn",
      count: upcomingWindow.failedSubs,
      title: t("attention.upcomingFailed", { days: UPCOMING_WINDOW_DAYS }),
      to: "/billing/upcoming?filter=failedSubscription",
      destination: t("dest.upcoming"),
    });
  if (botStats.nearStopLoss.length > 0)
    attention.push({
      id: "nearStopLoss",
      severity: "crit",
      count: botStats.nearStopLoss.length,
      title: t("attention.nearStopLoss", { percent: NEAR_STOP_LOSS_RATIO * 100 }),
      to: `/stats/bots?filter=nearStopLoss&ids=${botStats.nearStopLoss.map((bot) => bot.id).join(",")}`,
      destination: t("dest.bots"),
    });
  if ((onLossPagination?.total ?? 0) > 0)
    attention.push({
      id: "onLoss",
      severity: "info",
      count: onLossPagination?.total,
      title: t("attention.onLoss"),
      to: "/stats/bots?filter=onLoss",
      destination: t("dest.bots"),
    });

  // Each source contributes its own rows as soon as it lands. Blocking the
  // whole panel on the slowest request (the paginated unpaid-charges sweep)
  // made "Needs attention" the last thing on the page to appear.
  //
  // Every source is listed, including the two bot counts: omitting one let the
  // panel claim "nothing needs your attention" while it was still loading.
  // `isRefreshing` counts too, because cached counts up to an hour old are
  // painted immediately on a reload.
  const attentionSources = [
    loadingUnpaid || refreshingUnpaid,
    loadingBots,
    loadingOnLoss,
    loadingStale,
    loadingManual,
    loadingOverdue,
    loadingUpcoming || refreshingUpcoming,
  ];
  const attentionPending = attentionSources.filter(Boolean).length;
  const attentionLoading = attentionSources.every(Boolean);

  const perMonthMap = useMemo(() => {
    const map: Record<number, Record<string, { charged?: number; pending?: number }>> = {};
    if (chargesPrevYear?.total?.perMonth) map[year - 1] = chargesPrevYear.total.perMonth;
    if (chargesYear?.total?.perMonth) map[year] = chargesYear.total.perMonth;
    return map;
  }, [chargesYear, chargesPrevYear, year]);

  return (
    <Container className="mt-4" fluid>
      <PageHeader title={t("title")} subtitle={`${t("subtitle")} · ${t("updated", { time: now.format("HH:mm") })}`}>
        <Button size="sm" color="secondary" onClick={() => window.location.reload()}>
          <i className="fas fa-sync-alt mr-2" />
          {t("refresh")}
        </Button>
      </PageHeader>

      <ServerBuildAlert />

      {/* KPI strip */}
      <Row className="mb-3" style={{ rowGap: 12 }}>
        <Col xs={12} sm={6} xl={2}>
          <KpiTile
            label={t("kpi.mrr", { month: now.format("MMM") })}
            value={eur(mrr)}
            delta={mrrDelta}
            detail={
              !canShowComparison
                ? t("kpi.mrrDetailNoComparison", { month: prevMonth.format("MMM") })
                : comparableDays === 1
                  ? t("kpi.mrrDetailSingleDay", { month: prevMonth.format("MMM") })
                  : t("kpi.mrrDetail", { month: prevMonth.format("MMM"), day: comparableDays })
            }
            icon="fas fa-chart-line"
            to="/stats/financial/mrr"
            isLoading={loadingCharges}
          />
        </Col>
        <Col xs={12} sm={6} xl={2}>
          <KpiTile
            label={t("kpi.unpaid")}
            value={eur(unpaid.amountEur)}
            tone={unpaid.amountEur > 0 ? "crit" : "ok"}
            detail={t("kpi.unpaidDetail", { users: unpaid.users, escalated: unpaid.escalated })}
            icon="fas fa-exclamation-triangle"
            to="/billing/missing-payments"
            isLoading={loadingUnpaid}
          />
        </Col>
        <Col xs={12} sm={6} xl={2}>
          <KpiTile
            label={t("kpi.activeBots")}
            value={botStats.total}
            detail={t("kpi.activeBotsDetail", { onLoss: onLossPagination?.total ?? 0, stale: botStats.stale ?? 0 })}
            tone={(botStats.stale ?? 0) > 0 ? "warn" : "neutral"}
            icon="fas fa-robot"
            to="/stats/bots"
            isLoading={loadingBots}
          />
        </Col>
        <Col xs={12} sm={6} xl={2}>
          <KpiTile
            label={t("kpi.signups")}
            value={signups.total}
            delta={pct(signups.total, signups.prior)}
            detail={t("kpi.signupsDetail", { clients: signups.clients })}
            icon="fas fa-user-plus"
            to="/stats/users/sign-ups"
            isLoading={loadingSignups}
          />
        </Col>
        <Col xs={12} sm={6} xl={2}>
          <KpiTile
            label={t("kpi.upcoming", { days: UPCOMING_WINDOW_DAYS })}
            value={eur(upcomingWindow.amount)}
            detail={t("kpi.upcomingDetail", { count: upcomingWindow.count })}
            icon="fas fa-calendar-alt"
            to="/billing/upcoming"
            isLoading={loadingUpcoming}
          />
        </Col>
        <Col xs={12} sm={6} xl={2}>
          <KpiTile
            label={t("kpi.manual")}
            value={manual.total}
            tone={(manual.overdue ?? 0) > 0 ? "warn" : "neutral"}
            detail={t("kpi.manualDetail", { overdue: manual.overdue ?? 0 })}
            icon="fas fa-hand-holding-usd"
            to="/billing/manual-payments"
            isLoading={loadingManual}
          />
        </Col>
      </Row>

      <Row style={{ rowGap: 16 }}>
        <Col xs={12} xl={5}>
          <AttentionQueue
            title={t("attention.title")}
            items={attention}
            isLoading={attentionLoading}
            isPartial={attentionPending > 0}
            partialText={t("attention.partial")}
            hasError={
              !!(unpaidError || botsError || onLossError || staleError || manualError || overdueError || upcomingError)
            }
            errorText={t("attention.sourceFailed")}
            emptyText={t("attention.empty")}
          />
        </Col>
        <Col xs={12} xl={7}>
          <div style={styles.panel}>
            <div style={styles.panelHead}>
              <span style={styles.panelTitle}>{t("revenue.title")}</span>
              <span style={styles.panelMeta}>
                {t("revenue.legend", { charged: eur(chargesYear?.total?.totalCharged ?? 0), pending: eur(chargesYear?.total?.totalPending ?? 0) })}
              </span>
            </div>
            <MonthlyEarningsGraph yearDataMap={perMonthMap} isLoading={loadingCharges} style={{ height: 300 }} />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const styles: Record<string, CSSProperties> = {
  panel: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 16px", height: "100%" },
  panelHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 8, flexWrap: "wrap" },
  panelTitle: { fontWeight: 700, fontSize: 14, color: "#111827" },
  panelMeta: { fontSize: 12, color: "#6b7280" },
};

export default Overview;
